import React, { useState, useEffect, useCallback } from 'react'
import { Form } from 'react-bootstrap'
import { useMainSiteContentNamesList } from '../../../../../api/main-site-content/main-site-content.hooks'

import Select from 'react-select';
import makeAnimated from 'react-select/animated';

const animatedComponents = makeAnimated();

const CategorySelector = ({ onSelectionChange, initialSelections = [] }) => {
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedSubCategories, setSelectedSubCategories] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)

  const { namesList, isLoading: isNamesLoading } = useMainSiteContentNamesList()
  const categories = namesList['attractions-by-category'] || []

  const subCategories = {
    "historical-and-archaeological": [
      "Indus Valley Civilization",
      "Gandhara Civilization",
      "Vedic and Early Hindu Period",
      "Mughal Era",
      "Sikh Architecture and Heritage",
      "Colonial Architecture",
      "Forts"
    ],
    "religious-and-spiritual": [
      "Islam",
      "Hinduism",
      "Buddhism",
      "Christianity",
      "Sikhism"
    ],
    "cultural-and-artistic": [
      "Museums and Culture Centers",
      "Traditional Crafts and Arts",
    ],
    "natural-and-eco": [
      "Hill Station and Mountains",
      "Lakes and Water Bodies",
      "River Wetlands and Barrages",
      "Natural Parks Forests and Wildlife",
      "Desert Tourism",
      "Dams and Headworks",
    ],
    "culinary-tourism": [
      "Food Destinations"
    ]
  }

  // Initialize selections from props only once
  useEffect(() => {
    if (!isInitialized && initialSelections.length > 0) {
      setSelectedCategories(initialSelections.map(item => item.name))
      setSelectedSubCategories(initialSelections.flatMap(item => item.subcategories || []))
    }
    setIsInitialized(true)
  }, [initialSelections, isInitialized])

  // Notify parent component of changes
  useEffect(() => {
    const selections = selectedCategories.map(category => ({
      name: category,
      subcategories: selectedSubCategories.filter(subCat => 
        subCategories[category]?.includes(subCat)
      )
    }))
        
    if (onSelectionChange) {
      onSelectionChange(selections)
    }
  }, [selectedCategories, selectedSubCategories, isInitialized, onSelectionChange])

  // Format options for react-select
  const categoryOptions = categories.map(category => ({
    value: category,
    label: category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }))

  const availableSubCategoryOptions = selectedCategories
    .flatMap(category => subCategories[category] || [])
    .map(subCategory => ({
      value: subCategory,
      label: subCategory
    }))

  const selectedCategoryValues = selectedCategories.map(category => ({
    value: category,
    label: category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }))

  const selectedSubCategoryValues = selectedSubCategories.map(subCategory => ({
    value: subCategory,
    label: subCategory
  }))

  const handleCategorySelectChange = useCallback((selectedOptions) => {
    const newCategories = selectedOptions ? selectedOptions.map(option => option.value) : []
    setSelectedCategories(newCategories)
    
    // Remove subcategories that don't belong to selected categories
    setSelectedSubCategories(prev => 
      prev.filter(subCat => 
        newCategories.some(category => subCategories[category]?.includes(subCat))
      )
    )
  }, [])

  const handleSubCategorySelectChange = useCallback((selectedOptions) => {
    const newSubCategories = selectedOptions ? selectedOptions.map(option => option.value) : []
    setSelectedSubCategories(newSubCategories)
  }, [])

  if (isNamesLoading) {
    return <div>Loading categories...</div>
  }

  return (
    <div className="mb-3">
      <Form.Label>Category</Form.Label>
      
      <div className="mb-3">
        <Form.Label className="small">Select Category and Subcategory</Form.Label>
        <div className="row g-2 mb-2">
          <div className="col-md-6">
            <Select
              closeMenuOnSelect={false}
              components={animatedComponents}
              value={selectedCategoryValues}
              onChange={handleCategorySelectChange}
              options={categoryOptions}
              placeholder="Choose categories..."
              isMulti
              isClearable
            />
          </div>
          
          <div className="col-md-6">
            <Select
              closeMenuOnSelect={false}
              components={animatedComponents}
              value={selectedSubCategoryValues}
              onChange={handleSubCategorySelectChange}
              options={availableSubCategoryOptions}
              placeholder="Choose subcategories..."
              isDisabled={selectedCategories.length === 0}
              isMulti
              isClearable
              noOptionsMessage={() => selectedCategories.length === 0 ? "Please select a category first" : "No subcategories available"}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default CategorySelector
